// Keep the cloud collection names and string IDs so records can move between
// the existing Supabase format and the local edition without rewriting IDs.
export const COLLECTIONS = [
  "classes",
  "students",
  "dailyRecords",
  "homeworkAssignments",
  "homeworkRecords",
  "calendarEvents",
  "homeroomRecords",
  "attendanceRecords",
  "homeroomHomeworkRecords",
  "studentRoutineItems",
  "seatingCharts",
  "quizzes",
  "quizSessions",
  "quizResponses",
  "quizAttempts",
  "personalNotes",
  "personalNotePages",
] as const;

export type Collection = (typeof COLLECTIONS)[number];
export const SCHEMA_VERSION = 12;
export const ARCHIVE_COLLECTIONS = ["quizzes", "quizSessions", "quizResponses", "quizAttempts"] as const;

// SQL identifiers are only ever selected from this fixed map, never from a
// caller-supplied collection name.
export const COLLECTION_TABLES: Record<Collection, string> = {
  classes: "local_classes",
  students: "local_students",
  dailyRecords: "local_daily_records",
  homeworkAssignments: "local_homework_assignments",
  homeworkRecords: "local_homework_records",
  calendarEvents: "local_calendar_events",
  homeroomRecords: "local_homeroom_records",
  attendanceRecords: "local_attendance_records",
  homeroomHomeworkRecords: "local_homeroom_homework_records",
  studentRoutineItems: "local_student_routine_items",
  seatingCharts: "local_seating_charts",
  quizzes: "local_quizzes",
  quizSessions: "local_quiz_sessions",
  quizResponses: "local_quiz_responses",
  quizAttempts: "local_quiz_attempts",
  personalNotes: "local_personal_notes",
  personalNotePages: "local_personal_note_pages",
};

export const PERSONAL_SCHEMA_SQL = `
  CREATE TABLE IF NOT EXISTS local_personal_notes (
    workspace_id TEXT NOT NULL, record_id TEXT NOT NULL, data_json TEXT NOT NULL,
    PRIMARY KEY (workspace_id, record_id),
    FOREIGN KEY (workspace_id) REFERENCES workspace_state(workspace_id) ON DELETE CASCADE
  );
  CREATE TABLE IF NOT EXISTS local_personal_note_pages (
    workspace_id TEXT NOT NULL, record_id TEXT NOT NULL, note_id TEXT NOT NULL, data_json TEXT NOT NULL,
    PRIMARY KEY (workspace_id, record_id),
    FOREIGN KEY (workspace_id, note_id) REFERENCES local_personal_notes(workspace_id, record_id) ON DELETE CASCADE
  );
  CREATE INDEX IF NOT EXISTS personal_pages_by_note ON local_personal_note_pages(workspace_id, note_id);
`;

export const SCHEMA_SQL = `
  CREATE TABLE IF NOT EXISTS local_meta (
    key TEXT PRIMARY KEY,
    value TEXT NOT NULL
  );
  CREATE TABLE IF NOT EXISTS schema_migrations (
    version INTEGER PRIMARY KEY,
    applied_at TEXT NOT NULL
  );
  CREATE TABLE IF NOT EXISTS workspace_state (
    workspace_id TEXT PRIMARY KEY,
    workspace_json TEXT NOT NULL,
    hidden_tools_json TEXT NOT NULL DEFAULT '[]'
  );
  CREATE TABLE IF NOT EXISTS workspace_records (
    workspace_id TEXT NOT NULL,
    collection TEXT NOT NULL,
    record_id TEXT NOT NULL,
    data_json TEXT NOT NULL,
    PRIMARY KEY (workspace_id, collection, record_id),
    FOREIGN KEY (workspace_id) REFERENCES workspace_state(workspace_id) ON DELETE CASCADE
  );
  CREATE INDEX IF NOT EXISTS workspace_records_by_collection
    ON workspace_records (workspace_id, collection);
`;

// Keep the full document in data_json for cloud interchange, but give every
// domain entity its own keyed table and indexed relational columns.
export const ENTITY_SCHEMA_SQL = `
  CREATE TABLE local_classes (
    workspace_id TEXT NOT NULL, record_id TEXT NOT NULL, data_json TEXT NOT NULL,
    PRIMARY KEY (workspace_id, record_id),
    FOREIGN KEY (workspace_id) REFERENCES workspace_state(workspace_id) ON DELETE CASCADE
  );
  CREATE TABLE local_students (
    workspace_id TEXT NOT NULL, record_id TEXT NOT NULL, class_id TEXT, student_number INTEGER,
    data_json TEXT NOT NULL, PRIMARY KEY (workspace_id, record_id),
    FOREIGN KEY (workspace_id) REFERENCES workspace_state(workspace_id) ON DELETE CASCADE,
    FOREIGN KEY (workspace_id, class_id) REFERENCES local_classes(workspace_id, record_id) ON DELETE CASCADE
  );
  CREATE INDEX local_students_by_class_number ON local_students(workspace_id, class_id, student_number);
  CREATE UNIQUE INDEX local_students_unique_number ON local_students(workspace_id, class_id, student_number)
    WHERE student_number IS NOT NULL;
  CREATE TABLE local_homework_assignments (
    workspace_id TEXT NOT NULL, record_id TEXT NOT NULL, class_id TEXT, record_date TEXT,
    data_json TEXT NOT NULL, PRIMARY KEY (workspace_id, record_id),
    FOREIGN KEY (workspace_id) REFERENCES workspace_state(workspace_id) ON DELETE CASCADE,
    FOREIGN KEY (workspace_id, class_id) REFERENCES local_classes(workspace_id, record_id) ON DELETE CASCADE
  );
  CREATE TABLE local_homework_records (
    workspace_id TEXT NOT NULL, record_id TEXT NOT NULL, class_id TEXT, assignment_id TEXT,
    data_json TEXT NOT NULL, PRIMARY KEY (workspace_id, record_id),
    FOREIGN KEY (workspace_id) REFERENCES workspace_state(workspace_id) ON DELETE CASCADE,
    FOREIGN KEY (workspace_id, class_id) REFERENCES local_classes(workspace_id, record_id) ON DELETE CASCADE,
    FOREIGN KEY (workspace_id, assignment_id) REFERENCES local_homework_assignments(workspace_id, record_id) ON DELETE CASCADE
  );
  CREATE INDEX local_homework_records_by_assignment ON local_homework_records(workspace_id, assignment_id);
  CREATE TABLE local_daily_records (
    workspace_id TEXT NOT NULL, record_id TEXT NOT NULL, class_id TEXT, record_date TEXT,
    data_json TEXT NOT NULL, PRIMARY KEY (workspace_id, record_id),
    FOREIGN KEY (workspace_id) REFERENCES workspace_state(workspace_id) ON DELETE CASCADE,
    FOREIGN KEY (workspace_id, class_id) REFERENCES local_classes(workspace_id, record_id) ON DELETE CASCADE
  );
  CREATE TABLE local_calendar_events (
    workspace_id TEXT NOT NULL, record_id TEXT NOT NULL, class_id TEXT, record_date TEXT,
    data_json TEXT NOT NULL, PRIMARY KEY (workspace_id, record_id),
    FOREIGN KEY (workspace_id) REFERENCES workspace_state(workspace_id) ON DELETE CASCADE,
    FOREIGN KEY (workspace_id, class_id) REFERENCES local_classes(workspace_id, record_id) ON DELETE CASCADE
  );
  CREATE TABLE local_homeroom_records (
    workspace_id TEXT NOT NULL, record_id TEXT NOT NULL, class_id TEXT, record_date TEXT,
    data_json TEXT NOT NULL, PRIMARY KEY (workspace_id, record_id),
    FOREIGN KEY (workspace_id) REFERENCES workspace_state(workspace_id) ON DELETE CASCADE,
    FOREIGN KEY (workspace_id, class_id) REFERENCES local_classes(workspace_id, record_id) ON DELETE CASCADE
  );
  CREATE TABLE local_attendance_records (
    workspace_id TEXT NOT NULL, record_id TEXT NOT NULL, class_id TEXT, record_date TEXT,
    data_json TEXT NOT NULL, PRIMARY KEY (workspace_id, record_id),
    FOREIGN KEY (workspace_id) REFERENCES workspace_state(workspace_id) ON DELETE CASCADE,
    FOREIGN KEY (workspace_id, class_id) REFERENCES local_classes(workspace_id, record_id) ON DELETE CASCADE
  );
  CREATE TABLE local_homeroom_homework_records (
    workspace_id TEXT NOT NULL, record_id TEXT NOT NULL, class_id TEXT, record_date TEXT,
    data_json TEXT NOT NULL, PRIMARY KEY (workspace_id, record_id),
    FOREIGN KEY (workspace_id) REFERENCES workspace_state(workspace_id) ON DELETE CASCADE,
    FOREIGN KEY (workspace_id, class_id) REFERENCES local_classes(workspace_id, record_id) ON DELETE CASCADE
  );
  CREATE TABLE local_student_routine_items (
    workspace_id TEXT NOT NULL, record_id TEXT NOT NULL, class_id TEXT,
    data_json TEXT NOT NULL, PRIMARY KEY (workspace_id, record_id),
    FOREIGN KEY (workspace_id) REFERENCES workspace_state(workspace_id) ON DELETE CASCADE,
    FOREIGN KEY (workspace_id, class_id) REFERENCES local_classes(workspace_id, record_id) ON DELETE CASCADE
  );
  CREATE TABLE local_seating_charts (
    workspace_id TEXT NOT NULL, record_id TEXT NOT NULL, class_id TEXT,
    data_json TEXT NOT NULL, PRIMARY KEY (workspace_id, record_id),
    FOREIGN KEY (workspace_id) REFERENCES workspace_state(workspace_id) ON DELETE CASCADE,
    FOREIGN KEY (workspace_id, class_id) REFERENCES local_classes(workspace_id, record_id) ON DELETE CASCADE
  );
  CREATE INDEX local_daily_records_by_class_date ON local_daily_records(workspace_id, class_id, record_date);
  CREATE INDEX local_homework_assignments_by_class_date ON local_homework_assignments(workspace_id, class_id, record_date);
  CREATE INDEX local_calendar_events_by_class_date ON local_calendar_events(workspace_id, class_id, record_date);
  CREATE INDEX local_homeroom_records_by_class_date ON local_homeroom_records(workspace_id, class_id, record_date);
  CREATE INDEX local_attendance_records_by_class_date ON local_attendance_records(workspace_id, class_id, record_date);
  CREATE INDEX local_homeroom_homework_by_class_date ON local_homeroom_homework_records(workspace_id, class_id, record_date);
`;

// Archived cloud quiz data is preserved for export and backup only.
// This migration does not release the local quiz workflow.
export const ARCHIVE_SCHEMA_SQL = `
  CREATE TABLE local_quizzes (
    workspace_id TEXT NOT NULL, record_id TEXT NOT NULL, class_id TEXT, quiz_id TEXT,
    session_id TEXT, student_id TEXT, record_date TEXT, data_json TEXT NOT NULL,
    PRIMARY KEY (workspace_id, record_id),
    FOREIGN KEY (workspace_id) REFERENCES workspace_state(workspace_id) ON DELETE CASCADE,
    FOREIGN KEY (workspace_id, class_id) REFERENCES local_classes(workspace_id, record_id) ON DELETE CASCADE
  );
  CREATE TABLE local_quiz_sessions (
    workspace_id TEXT NOT NULL, record_id TEXT NOT NULL, class_id TEXT, quiz_id TEXT,
    session_id TEXT, student_id TEXT, record_date TEXT, data_json TEXT NOT NULL,
    PRIMARY KEY (workspace_id, record_id),
    FOREIGN KEY (workspace_id) REFERENCES workspace_state(workspace_id) ON DELETE CASCADE,
    FOREIGN KEY (workspace_id, class_id) REFERENCES local_classes(workspace_id, record_id) ON DELETE CASCADE,
    FOREIGN KEY (workspace_id, quiz_id) REFERENCES local_quizzes(workspace_id, record_id) ON DELETE CASCADE
  );
  CREATE TABLE local_quiz_responses (
    workspace_id TEXT NOT NULL, record_id TEXT NOT NULL, class_id TEXT, quiz_id TEXT,
    session_id TEXT, student_id TEXT, record_date TEXT, data_json TEXT NOT NULL,
    PRIMARY KEY (workspace_id, record_id),
    FOREIGN KEY (workspace_id) REFERENCES workspace_state(workspace_id) ON DELETE CASCADE,
    FOREIGN KEY (workspace_id, class_id) REFERENCES local_classes(workspace_id, record_id) ON DELETE CASCADE,
    FOREIGN KEY (workspace_id, quiz_id) REFERENCES local_quizzes(workspace_id, record_id) ON DELETE CASCADE,
    FOREIGN KEY (workspace_id, session_id) REFERENCES local_quiz_sessions(workspace_id, record_id) ON DELETE CASCADE,
    FOREIGN KEY (workspace_id, student_id) REFERENCES local_students(workspace_id, record_id) ON DELETE CASCADE
  );
  CREATE TABLE local_quiz_attempts (
    workspace_id TEXT NOT NULL, record_id TEXT NOT NULL, class_id TEXT, quiz_id TEXT,
    session_id TEXT, student_id TEXT, record_date TEXT, data_json TEXT NOT NULL,
    PRIMARY KEY (workspace_id, record_id),
    FOREIGN KEY (workspace_id) REFERENCES workspace_state(workspace_id) ON DELETE CASCADE,
    FOREIGN KEY (workspace_id, class_id) REFERENCES local_classes(workspace_id, record_id) ON DELETE CASCADE,
    FOREIGN KEY (workspace_id, quiz_id) REFERENCES local_quizzes(workspace_id, record_id) ON DELETE CASCADE,
    FOREIGN KEY (workspace_id, session_id) REFERENCES local_quiz_sessions(workspace_id, record_id) ON DELETE CASCADE,
    FOREIGN KEY (workspace_id, student_id) REFERENCES local_students(workspace_id, record_id) ON DELETE CASCADE
  );
`;

export function assertCollection(value: string): asserts value is Collection {
  if (!COLLECTIONS.includes(value as Collection)) throw new Error(`未知資料集合：${value}`);
}

// Historical collections are kept only for lossless SQLite/backup recovery.
export function isRetiredCollection(name: string): name is "personalNotes" | "personalNotePages" {
  return name === "personalNotes" || name === "personalNotePages";
}
